import React from "react";
import { useAuth } from "store/AuthProvider";
import { useData } from "store/DataProvider";
import {
	FlagBag,
	FlagBagStatus,
	OfflineData,
	StatusCode,
} from "services/api/flagbags/types";
import {
	CheckOutSpareFlagBag,
	ErrorCodes,
	ErrorMessages,
	flushOfflineDataApi,
	getNumOfRemainedBankQuota,
	getStationDetail,
	updateSignleFlagBag,
} from "services/api";
import { enqueueSnackbar } from "notistack";
import useDataController from "hooks/useDataController";
import { useSync } from "store/SyncContext";
import {flagBagApi} from "services/api/flagbags";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { isErrorResponse } from "services/api/constants";
import { usePendingQueue, SyncStatus } from "hooks/usePendingQueue";
import useFlagBagLogController from "hooks/useFlagBagLogController";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";
import { dispatchFlagBagStatusSaved } from "utils/flagbagStatusEvents";
import { assertVolunteerCanReceiveSpareFlagbag } from "utils/volunteerSpareAssignment";
import {
	convertApiStatusToStatusCode,
	isCollectedServerStatus,
	isReservedFlagbag,
} from "utils/flagbagStatusUtils";

function offlineRecordTimeScore(record: OfflineData): number {
	if (typeof record.syncFailedAt === "number") return record.syncFailedAt;
	const ut = record.updatedAt;
	if (ut != null && ut !== "") {
		const n = parseFloat(String(ut));
		if (!Number.isNaN(n)) return n < 1e11 ? n * 1000 : n;
	}
	return 0;
}

function pickLatestOfflineRecordForBag(records: OfflineData[], flagBagID: string): OfflineData | undefined {
	const matches = records.filter((r) => r.flagBagID === flagBagID);
	if (matches.length === 0) return undefined;
	return matches.reduce((best, cur) => {
		const tb = offlineRecordTimeScore(best);
		const tc = offlineRecordTimeScore(cur);
		if (tc !== tb) return tc > tb ? cur : best;
		return String(cur.id ?? "") > String(best.id ?? "") ? cur : best;
	});
}

const useFlagBagController = () => {
	const auth = useAuth();
	const data = useData();
	const db = initIndexedDbIfMissingStore("records");
	const stationDB = initIndexedDbIfMissingStore("station");
	const flagBagDB = initIndexedDbIfMissingStore("flagbag");
	const offlineDB = initIndexedDbIfMissingStore("offline");
	const DataController = useDataController();
	const { isOnline } = useNetworkStatus();
	const { isSyncing, setIsSyncing, lastUpdateDate, setLastUpdateDate } = useSync();
	const { batchSync, resetSyncProgress } = usePendingQueue();
	const { logStatusUpdate } = useFlagBagLogController();

	// React.useEffect(() => {
	// 	console.log("useFlagBagController useEffect - " + new Date().toLocaleTimeString() + " " + isSyncing);
	// 	if (!auth.getStationId()) return;
	// 	initDB(auth.getStationId());
	// }, [auth.getStationId()]);

	const initDB = (stationId: string) => {
		if (isSyncing) {
			enqueueSnackbar("更新中...", { variant: "info" });
			return;
		}

		// Check if offline - if so, use local data directly
		if (!isOnline) {
			return (async () => {
				try {
					const updateValue = await db.getByID(stationId);
					const flagBags = await getFlagBags();
					
					if (updateValue && updateValue.isCount) {
						return { updateValue, flagBags };
					} else {
						// Recalculate if needed
						await reCalculateFlagBags(flagBags);
						const newUpdateValue = await db.getByID(stationId);
						const newFlagBags = await getFlagBags();
						return { updateValue: newUpdateValue, flagBags: newFlagBags };
					}
				} catch (error) {
					console.error("initDB: failed to load local data", error);
					return { updateValue: null, flagBags: [] };
				}
			})();
		}

		return DataController.updateFlagBagAction(stationId)
			.then(async (resp) => {
				// Update last sync time from response
				if (resp?.LastUpdateTimeLocalTime) {
					const lastDate = new Date(resp.LastUpdateTimeLocalTime);
					setLastUpdateDate(lastDate);
				}
				// Fetch and update station detail with proper error handling
				try {
					const stationDetailResp = await getStationDetail(stationId, data.getEventId(), true);
					
					// httpClient.handleResponse extracts the 'data' field from IResponse
					// so stationDetailResp is directly the StationDetail object
					if (stationDetailResp && (stationDetailResp as any).id) {
						stationDB.update(stationDetailResp as any);
					} else {
						console.warn('Station detail missing id field. Response:', stationDetailResp);
					}
				} catch (error) {
					console.error('Failed to fetch station detail:', error);
					// Continue with flag bag updates even if station detail fails
				}
				
				// Check if resp has flagbags or items (new API structure)
				const flagBagsList = resp.items || resp.flagbags || [];
				
				if (flagBagsList.length === 0) {
					console.warn("No flag bags found in response");
					return { updateValue: { id: stationId, ...resp, isCount: false }, flagBags: [] };
				}
				
				// Directly use flag bags from the list API, no need to fetch status for each one
				const flagBags: FlagBag[] = [];
				
				for (const flagBag of flagBagsList) {
					const statusCode =
						typeof flagBag.status === "string"
							? convertApiStatusToStatusCode(flagBag.status)
							: StatusCode.NOT_ASSIGN;
					
					const processedFlagBag = {
						...flagBag,
						stationId: stationId,
						stationName: flagBag.stationName || data.getStationName(),
						status: statusCode,
						currentStatus: statusCode,
						allowedStatusList: [],
					};
					
					// Update flag bag database
					await flagBagDB.update(processedFlagBag);
					flagBags.push(processedFlagBag);
				}
				
				// Create updateValue with proper structure
				const updateValue = {
					...resp,
					id: stationId,
					flagbags: flagBagsList, // Normalize to flagbags
					...calculateFlagBags(resp, flagBags),
				};
				
				db.update(updateValue);
				
				return { updateValue, flagBags: flagBags };
			})
			.catch(async (error) => {
				if (error.errorCode === ErrorCodes.NETWORK_ERROR) {
					const updateValue = await db.getByID(stationId);
					const flagBags = await getFlagBags();
					if (updateValue.isCount) {
						return { updateValue: await db.getByID(stationId), flagBags: flagBags };
					} else {
						reCalculateFlagBags(flagBags);
						const newUpdateValue = await db.getByID(stationId);
						const newFlagBags = await getFlagBags();
						return { updateValue: newUpdateValue, flagBags: newFlagBags };
					}
				} else {
					return error;
				}
			});
	};

	const calculateFlagBags = (data: any, flagBags: FlagBag[]) => {
		const flagBagsByStatus = getFlagBagsByStatus(flagBags);
		// 旗袋总数按本站全部旗袋统计，不排除 assigned/spare 等状态或类型
		const total = flagBags.length;
		const flagBagPreassign = data.numOfPreAssignToStation;

		const receivedTotal =
			countFlagBag(flagBagsByStatus[StatusCode.COLLECTED]) +
			countFlagBag(flagBagsByStatus[StatusCode.BANK]) +
			countFlagBag(flagBagsByStatus[StatusCode.COLLECTED_EMPTY]) +
			countFlagBag(flagBagsByStatus[StatusCode.COLLECTED_OTHER]);

		const notReceivedTotal =
			countFlagBag(flagBagsByStatus[StatusCode.DISTRIBUTED]) +
			countFlagBag(flagBagsByStatus[StatusCode.DISTRIBUTED_OTHER]) +
			countFlagBag(flagBagsByStatus[StatusCode.LOSS]);
		const receivedList = {
			[StatusCode.COLLECTED]: { label: "實收", value: countFlagBag(flagBagsByStatus[StatusCode.COLLECTED]) },
			[StatusCode.BANK]: {
				label: "入銀行(限額)",
				value: `${countFlagBag(flagBagsByStatus[StatusCode.BANK])} (${data.totalBankQuota})`,
			},
			[StatusCode.COLLECTED_EMPTY]: { label: "吉袋", value: countFlagBag(flagBagsByStatus[StatusCode.COLLECTED_EMPTY]) },
			[StatusCode.COLLECTED_OTHER]: { label: "其他", value: countFlagBag(flagBagsByStatus[StatusCode.COLLECTED_OTHER]) },
		};
		const unreceivedList = {
			[StatusCode.DISTRIBUTED]: { label: "未收", value: countFlagBag(flagBagsByStatus[StatusCode.DISTRIBUTED]) },
			[StatusCode.DISTRIBUTED_OTHER]: {
				label: "遲收/遺失/其他",
				value:
					countFlagBag(flagBagsByStatus[StatusCode.DISTRIBUTED_OTHER]) +
					countFlagBag(flagBagsByStatus[StatusCode.LATE]) +
					countFlagBag(flagBagsByStatus[StatusCode.LOSS]),
			},
		};

		const extra = 0;
		const less = 0;
		const assign = countFlagBag(flagBagsByStatus[StatusCode.ASSIGNED]);
		const reserve = flagBags.filter((bag) => isReservedFlagbag(bag)).length;
		const updateValue = {
			isCount: true,
			total,
			receivedTotal,
			notReceivedTotal,
			flagBagPreassign,
			receivedList,
			unreceivedList,
			extra,
			less,
			assign,
			reserve,
		};
		return updateValue;
	};

	const reCalculateFlagBags = async (flagBags?: any[]) => {
		// console.log("reCalculateFlagBags - " + new Date().toLocaleTimeString());
		if (!flagBags) flagBags = (await flagBagDB.getAll()).filter((flagbag) => flagbag.stationId === data.getStationId());
		return new Promise((res, rej) => {
			getFlagBagDetail((cachedData) => {
				const filteredFlagbags = flagBags.filter((flagbag) => flagbag.stationId === data.getStationId());

				const updateValue = { ...cachedData, ...calculateFlagBags(cachedData, filteredFlagbags) };
				//console.log(updateValue);
				db.update(updateValue);
				res(updateValue);
			}, true);
		});
	};

	const getFlagBagsByStatus = (flagBags: FlagBag[]) => {
		const flagBagsByStatus = {};
		for (const key of Object.values(StatusCode)) {
			flagBagsByStatus[key] = [];
		}
		for (const flagBag of flagBags) {
			if (flagBag.status !== null) {
				flagBagsByStatus[flagBag.status].push(flagBag);
			}
		}
		return flagBagsByStatus;
	};

	const parseJsonObjectToFlagBagList = (obj: FlagBag) => {
		return { ...obj, status: obj.status };
	};

	const countFlagBag = (list) => {
		if (!list) return 0;

		return list.filter((item) => {
			return !item.isCollectByOtherStation;
		}).length;
	};

	const FlagBagStatus = async (flagBagId: string) => {
		return new Promise<FlagBagStatus>((res, rej) => {
			flagBagApi.getSingleFlagBag(data.getEventId(), flagBagId)
				.then((resp) => {
					// resp is already the FlagBag data (httpClient.get extracts 'data' field)
					const flagBag = resp as any as FlagBag;
					
					let currentStatus: StatusCode;
					if (typeof flagBag.status === 'string') {
						currentStatus = convertApiStatusToStatusCode(flagBag.status);
						if (currentStatus === StatusCode.NOT_ASSIGN && flagBag.status !== 'unassigned') {
							console.warn(`无法识别的状态字符串: ${flagBag.status}, 使用默认值 NOT_ASSIGN`);
						}
					} else {
						currentStatus = StatusCode.NOT_ASSIGN;
					}
					
					const allowedStatusList = DataController.getComingStatusListLocal(currentStatus);
					
					const flagBagStatus: FlagBagStatus & { stationId?: string; stationName?: string } = {
						flagBagId: flagBagId,
						currentStatus: currentStatus,
						flagbagNum: flagBag.flagbagNum,
						volunteerName: flagBag.volunteerName || '',
						allowedStatusList: allowedStatusList,
						bagType: flagBag.bagType,
						stationId: flagBag.stationId,
						stationName: flagBag.stationName,
					};
					res(flagBagStatus);
				})
				.catch((error) => {
					rej({ errorCode: error.errorCode, errorMsg: error.errorMsg });
				});
		});
	};

	const getStationData = async () => {
		const cachedData = await stationDB.getByID(data.getStationId());

		if (cachedData) {
			return cachedData;
		} else {
			try {
				const result = await getStationDetail(data.getStationId(), data.getEventId(), true);
				
				// httpClient.handleResponse extracts the 'data' field from IResponse
				// so result is directly the StationDetail object
				if (result && (result as any).id) {
					return result as any;
				} else {
					console.warn('getStationData: Station detail missing id field:', result);
					return null;
				}
			} catch (error) {
				console.error('getStationData: Failed to fetch station detail:', error);
				return null;
			}
		}
	};

	const getFlagBagDetail = async (callback: (data) => void, force: boolean = false) => {
		const stationId = data.getStationId();
		if (!stationId) {
			console.warn('getFlagBagDetail: No station ID available');
			return;
		}
		//console.log("run_getFlagBagDetail - " + new Date().toLocaleTimeString());

		let cachedData = await db.getByID(stationId);

		if (!cachedData) {
			try {
				await initDB(stationId);
				cachedData = await db.getByID(stationId);
				if (!cachedData) {
					enqueueSnackbar("取得資料失敗", { variant: "error" });
					return;
				}
			} catch (error) {
				console.error('getFlagBagDetail: Failed to initialize DB:', error);
				enqueueSnackbar("取得資料失敗", { variant: "error" });
				return;
			}
		} else if (!cachedData.isCount && !force) {
			try {
				cachedData = await reCalculateFlagBags();
			} catch (error) {
				console.error('getFlagBagDetail: Failed to recalculate flag bags:', error);
				// Continue with existing cachedData
			}
			//console.log(cachedData);
		}
		
		if (cachedData) {
			return await callback(cachedData);
		}
	};
	const getFlagBags = async () => {
		const stationId = data.getStationId();
		const eventId = data.getEventId();
		
		const allFlagbags = await flagBagDB.getAll();
		
		if (allFlagbags.length > 0) {
			const filteredFlagbags = allFlagbags.filter((flagbag) => flagbag.stationId === stationId);
			
			// Remove duplicates based on flagbag ID
			const uniqueFlagbags = Array.from(
				new Map(filteredFlagbags.map(fb => [fb.id, fb])).values()
			);
			
			return uniqueFlagbags;
		} else {
			// Check if offline - if so, return empty array without API call
			if (!isOnline) {
				return [];
			}
			
			// Validate required parameters before making API call
			if (!eventId || !stationId) {
				console.warn("getFlagBags: missing required parameters", { eventId, stationId });
				enqueueSnackbar("缺少活動或站點資訊，無法獲取旗袋列表", { variant: "warning" });
				return [];
			}
			
			try {
				const responseData = await flagBagApi.getFlagBagList(eventId, stationId, 1, 9000);
				const flagBags = responseData.items;
				
				flagBags.forEach((flagBag) => {
					flagBagDB.update({ ...flagBag, stationId: stationId });
				});
				return flagBags;
			} catch (error: any) {
				console.error("getFlagBags: API error", error);
				enqueueSnackbar(error.errorMsg || "獲取旗袋列表失敗", { variant: "error" });
				return [];
			}
		}
	};
	const getBag = async (flagBagId: string): Promise<FlagBagStatus & FlagBag> => {
		const data = await flagBagDB.getByID(flagBagId);
		return data;
	};
	const hasBag = async (flagBagId: string): Promise<boolean> => {
		const data = await flagBagDB.getByID(flagBagId);
		return data;
	};
	const addFlagBag = (flagBags: FlagBag[]) => {
		flagBags.forEach((flagBag) => {
			flagBagDB.update(flagBag);
		});
		getFlagBagDetail((data) => {
			const updateValue = { ...data, isCount: false };
			db.update(updateValue);
		});
	};
	const updateFlagBag = async (flagBags: FlagBag[]) => {
		flagBags.forEach((flagBag) => {
			flagBagDB.getByID(flagBag.id).then((data) => {
				FlagBagStatus(flagBag.id).then((resp) => {
					flagBagDB.update({ ...data, ...parseJsonObjectToFlagBagList(flagBag), ...resp });
				});
			});
		});
	};
	const deleteFlagBag = async (flagBags: FlagBag[]) => {
		flagBags.forEach((flagBag) => {
			flagBagDB.deleteRecord(flagBag.id);
		});
	};

	const setBagStatus = async (flagBagID: string, status: StatusCode) => {
		flagBagDB.getByID(flagBagID).then((data) => {
			flagBagDB.update({ ...data, status, currentStatus: status });
		});
		// Log status update
		await logStatusUpdate(flagBagID, status, data.getStationId());
		const flagBags = await getFlagBags();
		//reCalculateFlagBags(flagBags);
	};
	const setBagNamePhone = (flagBagID: string, name: string, phone: string) => {
		flagBagDB.getByID(flagBagID).then((data) => {
			flagBagDB.update({ ...data, volunteerName: name, volunteerPhone: phone });
		});
	};

	const checkExcessBankQuotaAndShowPopUp = (numOfBagToBeSetBank: number) => {
		return new Promise((res, rej) => {
			getNumOfRemainedBankQuota(data.getEventId(), data.getStationId())
				.then((resp) => {
					// online
					const remainedBankQuota = resp.remainedBankQuota;
					const excessQuotaTimestamp = resp.excessQuotaTimestamp;

					getFlagBagDetail((cachedData) => {
						if (
							remainedBankQuota - numOfBagToBeSetBank < 0 &&
							(excessQuotaTimestamp === 0 || cachedData.reachedBankQuotaPopUpTimestamp !== excessQuotaTimestamp)
						) {
							res({});
						} else {
							rej({ errorCode: 1, errorMsg: "noPopUp" });
						}
					});
				})
				.catch((error) => {
					if (error.errorCode === ErrorCodes.NETWORK_ERROR) {
						//offline

						getFlagBagDetail(async (cachedData) => {
							const flagBags = (await flagBagDB.getAll()).filter((flagbag) => flagbag.stationId === data.getStationId());
							const flagBagsByStatus = getFlagBagsByStatus(flagBags);

							const remainedBankQuota = cachedData.totalBankQuota - countFlagBag(flagBagsByStatus[StatusCode.BANK]);
							const excessQuotaTimestamp = 0;
							let enableBankQuotaPopUp = true;
							if (cachedData.reachedBankQuotaPopUpTimestamp !== excessQuotaTimestamp) enableBankQuotaPopUp = true;
							if (remainedBankQuota - numOfBagToBeSetBank < 0 && enableBankQuotaPopUp) {
								enableBankQuotaPopUp = false;
								res({});
							} else {
								rej({ errorCode: 1, errorMsg: "noPopUp" });
							}
						});
					}
				});
		});
	};

	//Offline Data Handler
	const flushOfflineData = async () => {
		const offlineDatas = await offlineDB.getAll();
		if (offlineDatas == null || offlineDatas.length === 0) {
			resetSyncProgress();
			return { success: true, hasErrors: false, totalSuccess: 0, totalFailed: 0 };
		}

		try {
			const eventId = data.getEventId();
			if (!eventId) {
				throw new Error("缺少活動ID");
			}

			// 使用新的 batchSync 函数（会自动处理成功/失败的数据）
			// 传入 flagBagDB 以便补充缺失的 flagbagNum
			const result = await batchSync(eventId, 50, flagBagDB);
			
		// 显示失败的错误消息
		if (result.errors && result.errors.length > 0) {
			// 显示具体的错误原因
			result.errors.forEach((error, index) => {
				setTimeout(() => {
					enqueueSnackbar(`${error.flagbagNum}：${error.reason}`, { 
						variant: "error",
						autoHideDuration: 6000
					});
				}, index * 500); // 错开显示时间，避免重叠
			});
		}

			// 更新同步时间
			await getFlagBagDetail((cachedData) => {
				const timeObj = {
					flagBagLastUpdateTime: Math.floor(Date.now() / 1000),
					LastUpdateTimeLocalTime: Date.now(),
				};
				const newData = { ...cachedData, ...timeObj };
				db.update(newData);
				
				// Update last sync time in SyncContext
				const lastDate = new Date(newData.LastUpdateTimeLocalTime);
				setLastUpdateDate(lastDate);

				return initDB(data.getStationId());
			});

			// 返回同步结果
			return {
				success: result.totalFailed === 0,
				hasErrors: result.totalFailed > 0,
				totalSuccess: result.totalSuccess,
				totalFailed: result.totalFailed,
				errors: result.errors
			};
		} catch (error) {
			console.error("Flush offline data error:", error);
			throw error;
		}
	};
	const addOfflineDataIndividual = async (flagBagID: string, status: StatusCode, isFreeUpdate: boolean) => {
		try {
			const stationId = data.getStationId();
			const cachedData = await db.getByID(stationId);
			const accountData = auth.getAccountData();
			
			// 获取 flagbag 信息以获得 flagbagNum 和 volunteerId
			const flagbag = await flagBagDB.getByID(flagBagID);
			const flagbagNum = flagbag?.flagbagNum || flagbag?.flagBagNum || flagBagID;
			const volunteerId = flagbag?.volunteerId || "";
			console.log('flagbag: ', flagbag);
			const offlineData = new OfflineData(
				stationId, 
				flagBagID, 
				"1", 
				"1", 
				status, 
				isFreeUpdate, 
				"", 
				"", 
				/*cachedData?.flagBagLastUpdateTime ||*/ (Date.now() / 1000).toString(),
				volunteerId,  // 使用 flagbag 的 volunteerId
				accountData?.userName,
				flagbagNum
			);
			offlineData.lastSyncedAt =
				flagbag?.lastUpdatedAt ||
				(cachedData?.flagBagLastUpdateTime != null
					? String(cachedData.flagBagLastUpdateTime)
					: undefined);
			
			// only add offlineData for collected statuses
			if (isCollectedServerStatus(status)) {
				offlineData.finalStationId = stationId;
			}
			const allOffline = await offlineDB.getAll();
			const existing = pickLatestOfflineRecordForBag(allOffline ?? [], flagBagID);
			if (existing) {
				offlineData.id = existing.id;
				offlineData.syncError = undefined;
				offlineData.syncFailedAt = undefined;
				const duplicateIds = (allOffline ?? [])
					.filter((r) => r.flagBagID === flagBagID && r.id !== existing.id)
					.map((r) => r.id);
				await Promise.all(duplicateIds.map((id) => offlineDB.deleteRecord(id)));
			}

			console.log("addofflineData: ", offlineData);
			await offlineDB.update(offlineData);
		} catch (error) {
			console.error("Failed to add offline data:", error);
		}
	};

	const addOfflineDataSpare = async (flagBagID: string, volunteerId: string, targetStatus?: StatusCode) => {
		await assertVolunteerCanReceiveSpareFlagbag(offlineDB, flagBagDB, volunteerId, flagBagID);
		try {
			const accountData = auth.getAccountData();
			const stationId = data.getStationId();
			const cachedData = await db.getByID(stationId);
			
			// 获取 flagbag 信息以获得 flagbagNum
			const flagbag = await flagBagDB.getByID(flagBagID);
			const flagbagNum = flagbag?.flagbagNum || flagbag?.flagBagNum || flagBagID;
			
			// 如果没有指定目标状态，则根据当前状态决定
			let statusToUse = targetStatus || StatusCode.DISTRIBUTED;
			if (!targetStatus) {
				const currentStatus = flagbag?.status || flagbag?.currentStatus;
				if (currentStatus === StatusCode.NOT_ASSIGN) {
					statusToUse = StatusCode.ASSIGNED;
				}
			}
			
			const updatedAt = cachedData?.flagBagLastUpdateTime || (Date.now() / 1000).toString();
			const offlineData = new OfflineData(
				stationId, 
				flagBagID, 
				"2", 
				"", 
				statusToUse,
				false, 
				"", // name - 不再需要
				"", // phone - 不再需要
				updatedAt,
				volunteerId,  // 使用传入的 volunteerId 作为 userId
				accountData?.userName,
				flagbagNum
			);
			offlineData.lastSyncedAt =
				flagbag?.lastUpdatedAt ||
				(cachedData?.flagBagLastUpdateTime != null
					? String(cachedData.flagBagLastUpdateTime)
					: undefined);
			// 调用 setStatus 确保时间戳字段正确设置（如 assignedAt 等）
			const statusString = statusToUse === StatusCode.ASSIGNED ? "assigned" : "distributed";
			offlineData.setStatus(statusString);
			offlineDB.update(offlineData);
		} catch (error) {
			console.error("Failed to add offline spare data:", error);
		}
	};
	const removeOfflineData = async (offlineData: OfflineData) => {
		const offlineDatas = await offlineDB.getAll();
		if (!offlineDatas) return false;

		if (offlineDatas.includes(offlineData)) {
			offlineDB.deleteRecord(offlineData.id);
			return true;
		} else return false;
	};
	const resetOfflineData = () => {
		offlineDB.clear();
	};

	const checkOutSpareFlagBag = async (flagBagID: string, volunteerId: string) => {
		try {
			await assertVolunteerCanReceiveSpareFlagbag(offlineDB, flagBagDB, volunteerId, flagBagID);

			const stationId = data.getStationId();
			const eventId = data.getEventId();
			if (!stationId) {
				throw { errorCode: -1, errorMsg: "無法獲取旗站ID" };
			}

			// Try to get cached data, but don't fail if it doesn't exist
			// (for cross-station operations, this may not be available)
			let cachedData = await db.getByID(stationId);
			
			// Fallback: search all records if not found by ID
			if (!cachedData) {
				const allRecords = await db.getAll();
				cachedData = allRecords.find((record: any) => 
					record.id === stationId || record.stationId === stationId
				);
			}
			
			// If still not found, try to initialize
			if (!cachedData) {
				try {
					await initDB(stationId);
					cachedData = await db.getByID(stationId);
					
					// Fallback again
					if (!cachedData) {
						const allRecords = await db.getAll();
						cachedData = allRecords.find((record: any) => 
							record.id === stationId || record.stationId === stationId
						);
					}
				} catch (error) {
					console.error("Failed to initialize DB:", error);
					// Continue anyway - we'll let the API call proceed
				}
			}

			// 获取当前旗袋状态，判断应该传递什么状态
			let targetStatus: StatusCode = StatusCode.DISTRIBUTED;
			try {
				const currentBag = await getBag(flagBagID);
				if (currentBag) {
					const currentStatus = currentBag.status || currentBag.currentStatus;
					// 如果当前状态是未分配（NOT_ASSIGN），则传递 assigned
					if (currentStatus === StatusCode.NOT_ASSIGN) {
						targetStatus = StatusCode.ASSIGNED;
					}
				}
			} catch (error) {
				console.warn("Failed to get current bag status, using default:", error);
			}

			const resp = await updateSignleFlagBag(
				flagBagID,
				eventId,
				stationId,
				{
					status: targetStatus,
					volunteerId: volunteerId,
				}
			);
			// Log status update
			await logStatusUpdate(flagBagID, targetStatus, stationId);
			initDB(stationId); // Fire and forget - don't await
			return resp;
		} catch (error: any) {
			if (error.errorCode !== undefined && error.errorCode !== ErrorCodes.NETWORK_ERROR) {
				throw error;
			}
			if (error.errorCode === ErrorCodes.NETWORK_ERROR) {
				// 获取当前旗袋状态，判断应该设置什么状态
				let targetStatusCode = StatusCode.DISTRIBUTED; // 默认状态
				try {
					const currentBag = await getBag(flagBagID);
					if (currentBag) {
						const currentStatus = currentBag.status || currentBag.currentStatus;
						// 如果当前状态是未分配（NOT_ASSIGN），则设置为 assigned
						if (currentStatus === StatusCode.NOT_ASSIGN) {
							targetStatusCode = StatusCode.ASSIGNED;
						}
					}
				} catch (bagError) {
					console.warn("Failed to get current bag status for offline mode, using default:", bagError);
				}

				addOfflineDataSpare(flagBagID, volunteerId, targetStatusCode);
				setBagStatus(flagBagID, targetStatusCode);
				// Log status update (offline)
				await logStatusUpdate(flagBagID, targetStatusCode, data.getStationId());
				// 更新本地旗袋的 volunteerId
				flagBagDB.getByID(flagBagID).then((flagbag) => {
					if (flagbag) {
						flagBagDB.update({ ...flagbag, volunteerId });
					}
				});
				// Log status update (offline)
				await logStatusUpdate(flagBagID, targetStatusCode, data.getStationId());
				return null;
			} else {
				// 显示错误消息，优先使用 API 返回的 message，否则使用错误代码对应的消息
				const errorMsg = error.errorMsg || ErrorMessages[error.errorCode] || "操作失敗";
				const errorCode = error.errorCode !== undefined ? `${error.errorCode}: ` : "";
				enqueueSnackbar(errorCode + errorMsg, { variant: "error" });
				throw error;
			}
		}
	};

	const updateFlagBagStatusToServerAdvance = async (
		flagBagId: string, 
		status: StatusCode | string, 
		currentStatus: string,
		isFreeUpdate: boolean
	) => {
		try {
			// Get station ID
			const stationId = data.getStationId();
			if (!stationId) {
				throw { errorCode: -1, errorMsg: "無法獲取旗站ID" };
			}

			// Try to get cached data, but don't fail if it doesn't exist
			// (for cross-station operations, this may not be available)
			let cachedData = await db.getByID(stationId);
			
			// Fallback: If not found by ID, search all records for matching stationId
			if (!cachedData) {
				const allRecords = await db.getAll();
				cachedData = allRecords.find((record: any) => 
					record.id === stationId || record.stationId === stationId
				);
			}

			// If still not found, try to initialize
			if (!cachedData) {
				try {
					await initDB(stationId);
					cachedData = await db.getByID(stationId);
					
					// Fallback again if still not found
					if (!cachedData) {
						const allRecordsAfterInit = await db.getAll();
						cachedData = allRecordsAfterInit.find((record: any) => 
							record.id === stationId || record.stationId === stationId
						);
					}
				} catch (error) {
					console.error("Failed to initialize DB:", error);
					// Continue anyway - we'll let the API call proceed
				}
			}

		const collectedStatuses: StatusCode[] = [
			StatusCode.COLLECTED,
			StatusCode.BANK,
			StatusCode.COLLECTED_EMPTY,
			StatusCode.COLLECTED_OTHER,
			StatusCode.LOSS,
			StatusCode.LATE,
		];
		const statusCode = typeof status === "string" ? convertApiStatusToStatusCode(status) : status;

		// Update flag bag status
		const resp = await updateSignleFlagBag(
			flagBagId, 
			data.getEventId(), 
			stationId, 
			{ 
				status: status, 
				currentStatus: currentStatus,
				...(collectedStatuses.includes(statusCode) && { finalStationId: stationId }),
			}
		);
		
		// Log status update
		await logStatusUpdate(flagBagId, statusCode, stationId);
			
			// Validate response structure
			if (!resp) {
				throw { errorCode: -1, errorMsg: "無效的服務器響應" };
			}
			
			// Check both possible response structures
			let responseData;
			if ('data' in resp && resp.data) {
				// Response has nested data field
				responseData = resp.data;
			} else {
				// Response is already the data
				responseData = resp;
			}
			
			// If the API returns updatedFlagbags structure (old format)
			if (responseData.updatedFlagbags) {
				if (responseData.updatedFlagbags.add) {
					await addFlagBag(responseData.updatedFlagbags.add);
				}
				if (responseData.updatedFlagbags.update) {
					await updateFlagBag(responseData.updatedFlagbags.update);
				}
			} else if (responseData.flagbag) {
				// If the API returns a single updated flagbag (new format)
				await updateFlagBag([responseData.flagbag]);
			}

			const timeObj = DataController.syncTime(responseData);
			// Provide default cachedData if not available (cross-station operations)
			const defaultCachedData = cachedData || { id: stationId };
			let updatedData = { ...defaultCachedData, ...timeObj, isCount: false };
			db.update(updatedData);
			
		// Update last sync time in SyncContext
		const lastDate = new Date(updatedData.LastUpdateTimeLocalTime);
		setLastUpdateDate(lastDate);

		// Return response in expected format
		const detail = responseData.detail || responseData.flagbag || { id: flagBagId };
		
		return {
			updatedFlagbags: responseData.updatedFlagbags || {
				update: responseData.flagbag ? [responseData.flagbag] : [],
				add: [],
				delete: []
			},
			detail: detail
		};
		} catch (error) {
			if ((error as any).errorCode === ErrorCodes.NETWORK_ERROR) {
				// Handle offline mode
				const stationId = data.getStationId();
				let cachedData = await db.getByID(stationId);
				
				// 如果 status 是 string（API code），需要转换为 StatusCode
				let statusCode: StatusCode;
				if (typeof status === 'string') {
					statusCode = convertApiStatusToStatusCode(status);
				} else {
					statusCode = status;
				}
				
				addOfflineDataIndividual(flagBagId, statusCode, false);
				const bag = await getBag(flagBagId);
				
				if (!bag) {
					throw { errorCode: -1, errorMsg: "找不到旗袋" };
				}
				
				setBagStatus(flagBagId, statusCode);
				// Log status update (offline)
				await logStatusUpdate(flagBagId, statusCode, stationId);
				const updateBag = { ...bag, status: statusCode };
				// Provide default cachedData if not available (cross-station operations)
				const defaultCachedData = cachedData || { id: stationId };
				let updatedData = { ...defaultCachedData, isCount: false };
				db.update(updatedData);
				
				return { 
					updatedFlagbags: { update: [updateBag], add: [], delete: [] }, 
					detail: updateBag 
				};
			} else {
				throw error;
			}
		}
	};
	return {
		initDB,
		reCalculateFlagBags,
		getStationData,
		getFlagBagDetail,
		getFlagBags,
		getBag,
		hasBag,
		FlagBagStatus,
		setBagStatus,
		setBagNamePhone,
		checkExcessBankQuotaAndShowPopUp,
		updateFlagBagStatusToServerAdvance,
		parseJsonObjectToFlagBagList,
		checkOutSpareFlagBag,
		flushOfflineData,
		addOfflineDataIndividual,
		addOfflineDataSpare,
	};
};

export default useFlagBagController;
